import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { usePieces } from '../hooks/usePieces';
import RelatedModal from '../components/RelatedModal';
import { useLang } from '../contexts/LanguageContext';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();
  const { pieces, loading: piecesLoading } = usePieces();
  const piece = pieces.find(p => p.id === id);
  const fromCollection = location.state?.fromCollection;

  const [phase, setPhase] = useState(0);
  const [, setBlackOverlay] = useState(fromCollection);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [imgKey, setImgKey] = useState(0);
  const [modalPiece, setModalPiece] = useState(null);

  useEffect(() => {
    setSelectedImageIdx(0);
    setImgKey(k => k + 1);
  }, [id]);

  useEffect(() => {
    if (fromCollection) {
      setPhase(2);
      setBlackOverlay(true);
      const t1 = setTimeout(() => setBlackOverlay(false), 100);
      return () => clearTimeout(t1);
    }
    setPhase(0);
    const t1 = setTimeout(() => setPhase(1), 50);
    const t2 = setTimeout(() => setPhase(2), 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [id, fromCollection]);

  if (piecesLoading) {
    return <div className="h-screen bg-black" />;
  }

  if (!piece) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <p className="font-serif text-2xl text-white mb-4">{t('collection.notFound')}</p>
          <Link to="/collection" className="text-sm text-white/40 hover:text-white transition-colors underline">
            {t('collection.backToCollection')}
          </Link>
        </div>
      </div>
    );
  }

  const mainImg = piece.images?.[selectedImageIdx] || piece.images?.[0] || piece.img;
  const related = piece.relatedProducts || [];

  const handleImageChange = (i) => {
    setSelectedImageIdx(i);
    setImgKey(k => k + 1);
  };

  return (
    <>
      {/* ════════════════════════════════════════════════════ */}
      {/* ── VERSION MOBILE / TABLETTE (< md) ── */}
      {/* ════════════════════════════════════════════════════ */}
      <div className="md:hidden bg-black min-h-screen pb-12">

        {/* IMAGE — carré en haut */}
        <div className="relative w-full aspect-square overflow-hidden">
          <img
            key={`m-img-${imgKey}`}
            src={mainImg}
            alt={piece.name}
            className="absolute inset-0 w-full h-full object-cover animate-imgReveal" />
          <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 100px 30px rgba(0,0,0,0.1)' }} />

          {/* Bouton retour — bien sous la navbar (qui fait ~112px) */}
          <button
            onClick={() => navigate('/collection')}
            className="absolute top-32 left-4 flex items-center gap-2 text-white/90 hover:text-white transition-all z-[70] group bg-black/40 backdrop-blur-md px-3 py-2 rounded">
            <span className="w-5 h-px bg-current transition-all duration-300 group-hover:w-8" />
            <span className="text-[10px] tracking-[0.2em] uppercase">{t('collection.back')}</span>
          </button>
        </div>

        {/* CONTENU SOUS L'IMAGE */}
        <div className={`px-6 pt-8 transition-opacity duration-700 ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>

          {/* Numéro de pièce */}
          <p className="text-[10px] tracking-[0.4em] text-white/25 uppercase text-center mb-6">
            {String(piece.num || 0).padStart(2, '0')} / {String(pieces.length).padStart(2, '0')}
          </p>

          {/* Nom */}
          <h1 className="font-serif text-3xl sm:text-4xl text-white font-light tracking-wide leading-tight text-center mb-8">
            {piece.name || t('collection.untitledPiece')}
          </h1>

          {/* Histoire — alignée à gauche pour la lisibilité */}
          {piece.story && (
            <p className="text-white/65 text-[14px] leading-[1.85] font-light text-left mb-10">
              {piece.story}
            </p>
          )}

          {/* Carrousel miniatures */}
          {piece.images?.length > 1 && (
            <div className="flex gap-3 justify-center mb-10 overflow-x-auto pb-2">
              {piece.images.map((img, i) => {
                const active = i === selectedImageIdx;
                return (
                  <button key={i} onClick={() => handleImageChange(i)}
                    className="overflow-hidden border transition-all duration-300 w-16 h-16 shrink-0"
                    style={{
                      borderColor: active ? '#C8A96E' : 'rgba(255,255,255,0.15)',
                      opacity: active ? 1 : 0.5,
                    }}>
                    <img src={img} alt=""
                      className="w-full h-full object-cover"
                      style={{ filter: active ? 'none' : 'brightness(0.7)' }} />
                  </button>
                );
              })}
            </div>
          )}

          {/* Détails pierre — grille 2x2 */}
          <div className="grid grid-cols-2 gap-y-6 gap-x-4 py-8 border-t border-b border-white/10 mb-10">
            <DetailMobile label={t('collection.stone')} value={piece.stone || '—'} />
            <DetailMobile label={t('collection.origin')} value={piece.origin || '—'} />
            <DetailMobile label={t('collection.size')} value={piece.size || '—'} />
            <DetailMobile
              label={t('collection.availability')}
              value={piece.available > 0 ? t('collection.pieceCount')(piece.available) : t('collection.onOrder')} />
          </div>

          {/* CTA */}
          <Link
            to={`/contact?piece=${encodeURIComponent(piece.name)}&pierre=${encodeURIComponent(piece.stone || '')}`}
            className="block w-full text-center px-8 py-4 border border-white/20 text-white/80 text-[11px] tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500 mb-12">
            {t('collection.ctaContact')}
          </Link>

          {/* Bijoux liés — liste verticale pleine largeur */}
          {related.length > 0 && (
            <div className="pt-6 border-t border-white/10">
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-5 text-center">
                {t('collection.relatedTitle')}
              </p>
              <div className="flex flex-col gap-3">
                {related.map((r, i) => (
                  <button key={`m-${r.id}-${i}`}
                    onClick={() => setModalPiece(r)}
                    className="flex items-center gap-4 p-3 border border-white/10 hover:border-white/30 transition-colors text-left">
                    <div className="w-16 h-16 overflow-hidden shrink-0">
                      <img src={r.img} alt={r.name}
                        className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-base text-white truncate">
                        {r.name || <span className="italic text-white/40">Sans titre</span>}
                      </p>
                      {r.stone && (
                        <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 mt-1 truncate">
                          {r.stone}
                        </p>
                      )}
                    </div>
                    <span className="text-white/30 text-lg shrink-0">›</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════ */}
      {/* ── VERSION DESKTOP (md+) — LAYOUT INCHANGÉ ── */}
      {/* ════════════════════════════════════════════════════ */}
      <div className="hidden md:block h-screen overflow-hidden bg-black relative">

        {/* IMAGE PLEIN ECRAN */}
        <img
          key={`d-img-${imgKey}`}
          src={mainImg}
          alt={piece.name}
          className="absolute inset-0 w-full h-full object-cover transition-all duration-[1.5s] ease-out animate-imgReveal"
          style={{
            transform: phase >= 1 ? 'scale(1)' : 'scale(1.15)',
            opacity: phase >= 1 ? 1 : 0,
            filter: 'none',
          }}
        />

        {/* Vignette */}
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.05)' }} />

        {/* BOUTON RETOUR */}
        <button
          onClick={() => navigate('/collection')}
          className={`absolute top-24 left-8 md:left-12 flex items-center gap-3 text-white/50 hover:text-white transition-all z-[70] group duration-700 ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          <span className="w-8 h-px bg-current transition-all duration-300 group-hover:w-14" />
          <span className="text-[11px] tracking-[0.2em] uppercase">{t('collection.back')}</span>
        </button>

        {/* NOM */}
        <div className={`absolute top-1/4 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[1.2s] ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light leading-none tracking-wide">
            {piece.name || t('collection.untitledPiece')}
          </h1>
        </div>

        {/* HISTOIRE */}
        <div className={`absolute top-1/2 left-8 md:left-12 lg:left-16 -translate-y-1/2 z-10 max-w-md transition-all duration-[1.2s] delay-200 ease-out ${phase >= 2 ? 'opacity-100 translate-y-[-50%]' : 'opacity-0 translate-y-[-40%]'}`}
          style={{ marginTop: '60px' }}>
          <p className="text-white/50 text-[15px] leading-[1.9] font-light">
            {piece.story}
          </p>
        </div>

        {/* DETAILS PIERRE */}
        <div className={`absolute bottom-36 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[1.2s] delay-400 ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex gap-10 flex-wrap">
            <DetailDesktop label={t('collection.stone')} value={piece.stone || '—'} />
            <DetailDesktop label={t('collection.origin')} value={piece.origin || '—'} />
            <DetailDesktop label={t('collection.size')} value={piece.size || '—'} />
            <DetailDesktop
              label={t('collection.availability')}
              value={piece.available > 0 ? t('collection.pieceCount')(piece.available) : t('collection.onOrder')} />
          </div>
        </div>

        {/* CTA */}
        <div className={`absolute bottom-10 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[1.2s] delay-500 ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Link to={`/contact?piece=${encodeURIComponent(piece.name)}&pierre=${encodeURIComponent(piece.stone || '')}`}
            className="inline-block px-8 py-3 border border-white/20 text-white/70 text-[11px] tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500">
            {t('collection.ctaContact')}
          </Link>
        </div>

        {/* CARROUSEL MINIATURES */}
        {piece.images?.length > 1 && (
          <div className={`absolute bottom-16 left-1/2 -translate-x-1/2 z-10 transition-all duration-[1.2s] delay-300 ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <div className="flex gap-3">
              {piece.images.map((img, i) => {
                const active = i === selectedImageIdx;
                return (
                  <button key={i} onClick={() => handleImageChange(i)}
                    className="overflow-hidden border transition-all duration-300 w-20 h-20 md:w-24 md:h-24"
                    style={{
                      borderColor: active ? '#C8A96E' : 'rgba(255,255,255,0.15)',
                      opacity: active ? 1 : 0.5,
                    }}>
                    <img src={img} alt=""
                      className="w-full h-full object-cover"
                      style={{ filter: active ? 'none' : 'brightness(0.7)' }} />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* BIJOUX LIÉS */}
        {related.length > 0 && (
          <div className={`absolute bottom-16 right-8 md:right-12 lg:right-16 z-10 transition-all duration-[1.2s] delay-300 ease-out ${phase >= 2 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}>
            <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3 text-right">
              {t('collection.relatedTitle')}
            </p>
            <div className="flex gap-3 justify-end">
              {related.map((r, i) => (
                <button key={`d-${r.id}-${i}`}
                  onClick={() => setModalPiece(r)}
                  className="group flex flex-col items-center gap-2 transition-all duration-300">
                  <div className="w-20 h-20 md:w-24 md:h-24 overflow-hidden border border-white/15 group-hover:border-white/40 transition-all duration-300">
                    <img src={r.img} alt={r.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/50 group-hover:text-white transition-colors duration-300">
                    {r.stone || r.name}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* NUMÉRO DE PIÈCE */}
        <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 z-10 transition-all duration-[1.2s] delay-300 ease-out ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          <p className="text-[10px] tracking-[0.4em] text-white/15 uppercase">
            {String(piece.num || 0).padStart(2, '0')} / {String(pieces.length).padStart(2, '0')}
          </p>
        </div>
      </div>

      {/* MODALE BIJOU LIÉ — partagée mobile + desktop */}
      <RelatedModal piece={modalPiece} onClose={() => setModalPiece(null)} />

      <style>{`
        @keyframes imgReveal {
          from { opacity: 0; transform: scale(1.05); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-imgReveal {
          animation: imgReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
      `}</style>
    </>
  );
}

function DetailMobile({ label, value }) {
  return (
    <div>
      <p className="text-[9px] tracking-[0.3em] uppercase text-white/30 mb-1.5">{label}</p>
      <p className="font-serif text-base text-white/85">{value}</p>
    </div>
  );
}

function DetailDesktop({ label, value }) {
  return (
    <div>
      <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">{label}</p>
      <p className="font-serif text-lg text-white/80">{value}</p>
    </div>
  );
}
